/**
 * Shared performance maths for the pick ledger (pick_history).
 * Used by the homepage stats strip and /track-record so both show identical,
 * live-computed figures. Nothing here is hardcoded.
 */

export interface ResolvedPickLike {
  status: string;
  current_pct: number | null;
}

export interface PickPerformance {
  /** Resolved picks (non-pending) count. */
  resolved: number;
  /** Wins by the published rule (status = 'win'). */
  wins: number;
  /** Hit rate % = wins / resolved, or null when nothing resolved. */
  winRate: number | null;
  /** Mean return of resolved picks that finished above entry (%). */
  avgWin: number | null;
  /** Mean return of resolved picks that finished below entry (%), negative. */
  avgLoss: number | null;
  /** Expectancy: average return per resolved pick (%), win/loss weighted. */
  expectancy: number | null;
}

export function computePickPerformance(rows: ResolvedPickLike[]): PickPerformance {
  const resolvedRows = rows.filter((r) => r.status !== "pending");
  const wins = resolvedRows.filter((r) => r.status === "win").length;
  const winRate = resolvedRows.length ? (wins / resolvedRows.length) * 100 : null;

  const withPct = resolvedRows.filter(
    (r) => r.current_pct !== null && Number.isFinite(Number(r.current_pct)),
  ) as { current_pct: number }[];
  const gains = withPct.filter((r) => Number(r.current_pct) > 0).map((r) => Number(r.current_pct));
  const drops = withPct.filter((r) => Number(r.current_pct) < 0).map((r) => Number(r.current_pct));
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

  const avgWin = mean(gains);
  const avgLoss = mean(drops);

  let expectancy: number | null = null;
  if (withPct.length) {
    const pWin = gains.length / withPct.length;
    const pLoss = drops.length / withPct.length;
    expectancy = pWin * (avgWin ?? 0) + pLoss * (avgLoss ?? 0);
  }

  return { resolved: resolvedRows.length, wins, winRate, avgWin, avgLoss, expectancy };
}

export const EXPECTANCY_EXPLAINER =
  "Expectancy is the average result per pick once wins and losses are blended together — what you'd expect to make (or lose) on a typical pick.";

export function formatPct(v: number | null, digits = 1): string {
  if (v === null) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
}
